import { useEffect, useState } from "react";
import { ShieldCheck, MapPin } from "lucide-react";
import { api } from "../lib/api";

export default function VerifiedResults() {
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        api.get("/cpl/verified-results").then(({ data }) => setProjects(data || [])).catch(() => {});
    }, []);

    if (projects.length === 0) return null;

    return (
        <section
            id="results"
            data-testid="verified-results-section"
            className="py-24 sm:py-32 px-6 sm:px-10 lg:px-16 border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto">
                <div className="max-w-2xl mb-14">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-px w-10 bg-[#D4AF37]" />
                        <span className="overline">Verified Campaign Results</span>
                    </div>
                    <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                        Real, verified <span className="italic text-[#D4AF37]">past performance</span>.
                    </h2>
                    <p className="mt-6 text-white/60 max-w-lg">
                        These are actual completed Lead Krafts campaigns — not testimonials, not the calculator's
                        estimates. Figures are exactly as delivered.
                    </p>
                </div>

                <div className="sm:hidden mb-2 text-[10px] text-white/35 uppercase tracking-widest">
                    ← Swipe to see full table →
                </div>
                <div className="overflow-x-auto" data-testid="verified-results-table">
                    <table className="w-full text-sm min-w-[820px]">
                        <thead>
                            <tr className="text-left border-b border-white/10 text-[10px] uppercase tracking-widest text-white/40">
                                <th className="py-3 pr-4 whitespace-nowrap">Builder</th>
                                <th className="py-3 pr-4 whitespace-nowrap">Project</th>
                                <th className="py-3 pr-4 whitespace-nowrap">Micro-market</th>
                                <th className="py-3 pr-4 whitespace-nowrap">Type</th>
                                <th className="py-3 pr-4 whitespace-nowrap">Price Band</th>
                                <th className="py-3 pr-4 text-right whitespace-nowrap">Leads</th>
                                <th className="py-3 pr-4 text-right whitespace-nowrap">CPL</th>
                                <th className="py-3 pr-4 text-right whitespace-nowrap">Site Visits</th>
                                <th className="py-3 text-right whitespace-nowrap">Closures</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.map((p) => (
                                <tr key={p.id} data-testid={`verified-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                                    <td className="py-4 pr-4 text-white/80 whitespace-nowrap">{p.builder}</td>
                                    <td className="py-4 pr-4 text-white font-medium whitespace-nowrap">{p.project_name}</td>
                                    <td className="py-4 pr-4 text-white/60 whitespace-nowrap"><MapPin size={11} className="inline mr-1 text-[#D4AF37]" />{p.micro_market}, {p.city}</td>
                                    <td className="py-4 pr-4 text-white/60 whitespace-nowrap">{p.property_type}</td>
                                    <td className="py-4 pr-4 text-white/60 whitespace-nowrap">{p.price_band_label}</td>
                                    <td className="py-4 pr-4 text-right text-white/80 whitespace-nowrap">{p.leads_delivered}</td>
                                    <td className="py-4 pr-4 text-right text-[#D4AF37] font-medium whitespace-nowrap">₹{p.actual_cpl}</td>
                                    <td className="py-4 pr-4 text-right text-white/80 whitespace-nowrap">{p.site_visits}</td>
                                    <td className="py-4 text-right text-white/80 whitespace-nowrap">{p.closures}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-6 flex items-start gap-2 text-[11px] text-white/40">
                    <ShieldCheck size={13} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.5} />
                    <span>
                        Verified past campaign performance. These figures feed the AI CPL engine's historical
                        anchors (see the calculator above) as high-confidence data points — separate from the
                        seed model used for unclassified micro-markets.
                    </span>
                </div>
            </div>
        </section>
    );
}
